/**
 * Espelho síncrono dos dados de auth para código não-React (interceptors,
 * fetch, módulos utilitários).
 *
 * O AuthContext é a ÚNICA fonte de verdade. Este módulo é apenas um cache
 * de leitura síncrona alimentado pelo Provider — não pode ser escrito por
 * outros lugares (por convenção, _setMirror só deve ser chamado pelo
 * AuthContext e pelos helpers internos do tokenManager).
 *
 * Fluxo:
 *   AuthProvider (mount/effect/event) → _setMirror() → variáveis privadas
 *   api.ts/interceptors                → getAccessTokenSync() → lê variável
 *   auth.ts (refresh)                  → getRefreshTokenSync()
 *   socketService.ts/utils             → getUserDataSync()
 *   toolsSso.ts                        → getSsoTokenSync()/getBaseBearerSync()
 *
 * Lazy-load: se algo ler antes do AuthProvider montar (caso raro durante
 * bootstrap síncrono), tentamos ler de sessionStorage uma vez. Depois disso,
 * o Provider passa a ser a fonte canônica de updates.
 */

const KEYS = {
	ACCESS_TOKEN: "cdr:auth:accessToken",
	REFRESH_TOKEN: "cdr:auth:refreshToken",
	USER_DATA: "cdr:auth:userData",
	SSO_TOKEN: "cdr:tools_sso:token",
	SSO_EXP: "cdr:tools_sso:exp",
	BASE_BEARER: "cdr:tools_sso:base_bearer",
} as const;

const LEGACY = {
	ACCESS_TOKEN: "accessToken",
	USER_DATA: "userData",
	SSO_TOKEN: "tools_sso_token",
} as const;

let currentAccessToken: string | null = null;
let currentRefreshToken: string | null = null;
let currentUserData: any | null = null;
let currentSsoToken: string | null = null;
let currentSsoExp: string | null = null;
let currentBaseBearer: string | null = null;
let lazyLoaded = false;

function lazyLoad(): void {
	if (lazyLoaded) return;
	lazyLoaded = true;
	
	currentAccessToken =
		sessionStorage.getItem(KEYS.ACCESS_TOKEN) ??
		sessionStorage.getItem(LEGACY.ACCESS_TOKEN);

	currentRefreshToken = sessionStorage.getItem(KEYS.REFRESH_TOKEN);
	const ud =
		sessionStorage.getItem(KEYS.USER_DATA) ??
		sessionStorage.getItem(LEGACY.USER_DATA);
	if (ud) {
		try {
			currentUserData = JSON.parse(ud);
		} catch {
			currentUserData = null;
		}
	}
	currentSsoToken =
		sessionStorage.getItem(KEYS.SSO_TOKEN) ??
		sessionStorage.getItem(LEGACY.SSO_TOKEN);
	currentSsoExp = sessionStorage.getItem(KEYS.SSO_EXP);
	currentBaseBearer = sessionStorage.getItem(KEYS.BASE_BEARER);

}

/**
 * Lê o accessToken de forma síncrona.
 * Para uso fora de componentes React (interceptors, fetch, eventos).
 * Dentro de componentes, use `useAccessToken()` do AuthContext.
 */
export function getAccessTokenSync(): string | null {
	lazyLoad();
	return currentAccessToken;
}

/**
 * Lê o refreshToken de forma síncrona.
 * Para uso em fluxos de refresh fora de componentes React.
 */
export function getRefreshTokenSync(): string | null {
	lazyLoad();
	return currentRefreshToken;
}

/**
 * Lê o userData parseado de forma síncrona.
 * Para uso em módulos utilitários (socketService, orderService etc.).
 * Dentro de componentes, use `useUserData()` do AuthContext.
 */
export function getUserDataSync(): any | null {
	lazyLoad();
	return currentUserData;
}

/**
 * Lê o ssoToken de forma síncrona.
 * Para uso em toolsSso.ts e fluxos SSO de `/tools`.
 */
export function getSsoTokenSync(): string | null {
	lazyLoad();
	return currentSsoToken;
}

/**
 * Lê a expiração do ssoToken (ISO string) de forma síncrona.
 */
export function getSsoExpSync(): string | null {
	lazyLoad();
	return currentSsoExp;
}

/**
 * Lê o baseBearer de forma síncrona.
 * Para uso em renovação do ssoToken (toolsSso.ts).
 */
export function getBaseBearerSync(): string | null {
	lazyLoad();
	return currentBaseBearer;
}

/**
 * Patch parcial do espelho. APENAS o AuthProvider e os setters do
 * tokenManager devem chamar isto.
 *
 * Aceita apenas as chaves presentes — undefined ≠ null:
 *   _setMirror({ accessToken: 'abc' })          // só accessToken muda
 *   _setMirror({ refreshToken: null })          // refreshToken vira null
 *   _setMirror({ userData: { ... } })           // só userData muda
 *   _setMirror({ ssoToken: 'x', ssoExp: 'y' })  // múltiplas chaves de uma vez
 */
export function _setMirror(patch: {
	accessToken?: string | null;
	refreshToken?: string | null;
	userData?: any | null;
	ssoToken?: string | null;
	ssoExp?: string | null;
	baseBearer?: string | null;
}): void {
	lazyLoaded = true;
	if ("accessToken" in patch) {
		currentAccessToken = patch.accessToken ?? null;
	}
	if ("refreshToken" in patch) {
		currentRefreshToken = patch.refreshToken ?? null;
	}
	if ("userData" in patch) {
		currentUserData = patch.userData ?? null;
	}
	if ("ssoToken" in patch) {
		currentSsoToken = patch.ssoToken ?? null;
	}
	if ("ssoExp" in patch) {
		currentSsoExp = patch.ssoExp ?? null;
	}
	if ("baseBearer" in patch) {
		currentBaseBearer = patch.baseBearer ?? null;
	}
}
